import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { DownloadOutlined } from '@ant-design5/icons';
import { Button } from 'components/ui';

import { ValidateImageMetadataFileMutationResult } from 'types/atlas-graphql';
import { Steps } from '../UploadInspectionDataModal';
import { AdditionalInfoText, ButtonContainer } from '../UploadInspectionDataModal.style';
import { useUploadInspectionDataModalContext } from '../Context/UploadInspectionDataModalContext';

export type ImageDetailsValidationProps = ValidateImageMetadataFileMutationResult['data'] & {
  fileName: string;
  setStep: Dispatch<SetStateAction<Steps>>;
  setErroredUploadSteps: Dispatch<
    SetStateAction<{
      summary: boolean;
      imageDetails: boolean;
    }>
  >;
};

export const Validation: React.FC<ImageDetailsValidationProps> = ({
  validateImageMetadataFile,
  fileName,
  setStep,
  setErroredUploadSteps,
}) => {
  const { setAlertState } = useUploadInspectionDataModalContext();

  useEffect(() => {
    if (validateImageMetadataFile) {
      setAlertState({
        alertTitle: `${failureCount} row${failureCount === 1 ? '' : 's'} in ${fileName} contained invalid or incomplete data.`,
        alertType: 'error',
        alertDescription:
          'Please use the button below to download the error file. Then complete the missing data and upload again.',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateImageMetadataFile]);

  if (!validateImageMetadataFile) {
    return null;
  }
  const { draftImages, errorFileUrl, failureCount } = validateImageMetadataFile;

  const handleClickReupload = () => {
    setAlertState(null);
    setStep(Steps.DetailsUpload);
    setErroredUploadSteps(uploadErrors => ({ ...uploadErrors, imageDetails: false }));
  };

  return (
    <>
      <p>
        {draftImages?.length ?? 0} image detail mapping{draftImages.length === 1 ? '' : 's'}{' '}
        validated.
      </p>
      <Button icon={<DownloadOutlined />} href={errorFileUrl}>
        Download Error File
      </Button>
      <AdditionalInfoText>
        Note: Horizon requires the image details to be formatted in a way that is compatible with
        our system. For more information on configured inspection details uploads, please visit our{' '}
        <a
          href="https://guide.skyspecs.com/horizon/-MPobGVTLxgvA9kxtck7/"
          target="_blank"
          rel="noopener noreferrer"
        >
          User Guide
        </a>
        .
      </AdditionalInfoText>
      <ButtonContainer>
        <Button type="primary" onClick={handleClickReupload}>
          Reupload
        </Button>
      </ButtonContainer>
    </>
  );
};
